import { useState } from "preact/hooks";
import { ArrowRightIcon } from "./components.tsx";

export type ComparisonItem = {
  category: string;
  feature: string;
  prompt2bot: {
    highlight: string;
    description: string;
    status: "positive" | "neutral" | "negative";
  };
  openclaw: {
    highlight: string;
    description: string;
    status: "positive" | "neutral" | "negative";
  };
  hermes: {
    highlight: string;
    description: string;
    status: "positive" | "neutral" | "negative";
  };
};

export const comparisonData: readonly ComparisonItem[] = [
  {
    category: "Architecture & Multi-Tenancy",
    feature: "Multi-User Data Isolation",
    prompt2bot: {
      highlight: "Structural isolation (`isolateUsers = true`)",
      description:
        "Per-conversation memory isolation enforced at the schema level. Zero risk of client data or file leaks.",
      status: "positive",
    },
    openclaw: {
      highlight: "Shared workspace directory",
      description:
        "Single gateway auth token and shared workspace. Files from one user session can leak to another.",
      status: "negative",
    },
    hermes: {
      highlight: "Global `USER.md` memory file",
      description:
        "Stores preferences in one global file injected into every conversation, causing cross-user contamination.",
      status: "negative",
    },
  },
  {
    category: "Cost & Compute",
    feature: "Idle Cost & Scaling",
    prompt2bot: {
      highlight: "Zero idle cost (serverless edge)",
      description:
        "Stateless edge run loop executes only on incoming events. Create unlimited free agents without paying for idle VMs.",
      status: "positive",
    },
    openclaw: {
      highlight: "Heavy persistent processes",
      description:
        "Requires dedicated persistent containers or VPS per bot, paying 24/7 server bills even when idle.",
      status: "negative",
    },
    hermes: {
      highlight: "Tied to host runtime",
      description:
        "Designed for single-machine local execution or dedicated server instances.",
      status: "neutral",
    },
  },
  {
    category: "Security & Sandboxing",
    feature: "Code Execution & Secret Egress",
    prompt2bot: {
      highlight: "Safescript + On-Demand VMs",
      description:
        "Domain-level firewall for credentials (e.g. Stripe keys only go to Stripe) + ephemeral VMs that auto-destruct.",
      status: "positive",
    },
    openclaw: {
      highlight: "Direct root shell access",
      description:
        "Agent has raw host shell access without outbound network firewalling, vulnerable to malicious supply-chain scripts.",
      status: "negative",
    },
    hermes: {
      highlight: "Unrestricted local terminal",
      description:
        "Executes scripts directly on local operating system with full access to local filesystem and environment.",
      status: "negative",
    },
  },
  {
    category: "Safety & Governance",
    feature: "Human-in-the-Loop Approvals",
    prompt2bot: {
      highlight: "Secure Admin Approvals",
      description:
        "When talking to third parties, sensitive tool execution pauses and requests 1-tap owner approval via WhatsApp/chat.",
      status: "positive",
    },
    openclaw: {
      highlight: "No multi-tenant authorization",
      description:
        "Any interacting user can trigger full agent tool capabilities unless custom middleware is written.",
      status: "negative",
    },
    hermes: {
      highlight: "No external verification gate",
      description:
        "Lacks role-based permission verification when interacting across untrusted external contacts.",
      status: "negative",
    },
  },
  {
    category: "Messaging & Channels",
    feature: "Chat-Native Multi-Channel",
    prompt2bot: {
      highlight: "Native WhatsApp, Telegram, E2EE, Email",
      description:
        "Built-in WhatsApp (Official + Supergreen), Telegram, Alice & Bot (E2EE), AgentMail, quote replies, and emoji reactions.",
      status: "positive",
    },
    openclaw: {
      highlight: "Webchat & terminal first",
      description:
        "Requires complex self-hosted bridge architecture and manual webhook configuration for messaging apps.",
      status: "neutral",
    },
    hermes: {
      highlight: "CLI / terminal wrappers",
      description:
        "Primarily local CLI oriented with basic community chat adapters.",
      status: "neutral",
    },
  },
  {
    category: "Deployment & UX",
    feature: "Time to Production",
    prompt2bot: {
      highlight: "< 1 minute setup (AI Builder)",
      description:
        "Conversational AI builder creates prompt, tools, and channel connections automatically. 1-click Talk-to-Skill launcher.",
      status: "positive",
    },
    openclaw: {
      highlight: "Manual orchestration",
      description:
        "Requires terminal setup, Docker image building, gateway token management, and infrastructure maintenance.",
      status: "neutral",
    },
    hermes: {
      highlight: "Local developer setup",
      description:
        "Requires Python environment setup, local API key configuration, and manual tool wiring.",
      status: "neutral",
    },
  },
] as const;

const CheckIcon = () => (
  <svg
    className="w-4 h-4 text-emerald-500 flex-shrink-0"
    viewBox="0 0 20 20"
    fill="currentColor"
  >
    <path
      fillRule="evenodd"
      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
      clipRule="evenodd"
    />
  </svg>
);

const WarningIcon = () => (
  <svg
    className="w-4 h-4 text-amber-500 flex-shrink-0"
    viewBox="0 0 20 20"
    fill="currentColor"
  >
    <path
      fillRule="evenodd"
      d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
      clipRule="evenodd"
    />
  </svg>
);

const CrossIcon = () => (
  <svg
    className="w-4 h-4 text-rose-500 flex-shrink-0"
    viewBox="0 0 20 20"
    fill="currentColor"
  >
    <path
      fillRule="evenodd"
      d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
      clipRule="evenodd"
    />
  </svg>
);

const StatusIcon = (
  { status }: { status: "positive" | "neutral" | "negative" },
) => {
  if (status === "positive") return <CheckIcon />;
  if (status === "neutral") return <WarningIcon />;
  return <CrossIcon />;
};

const cellBadgeClass = (status: "positive" | "neutral" | "negative") => {
  if (status === "positive") {
    return "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20";
  }
  if (status === "neutral") {
    return "bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20";
  }
  return "bg-rose-500/10 text-rose-600 dark:text-rose-400 border border-rose-500/20";
};

export const FeatureComparisonTable = () => {
  const [activeTab, setActiveTab] = useState<
    "desktop" | "prompt2bot" | "openclaw" | "hermes"
  >("desktop");

  return (
    <div className="w-full">
      {/* Mobile Platform Switcher */}
      <div className="flex md:hidden justify-center gap-1.5 p-1 rounded-xl bg-surface-overlay-light dark:bg-surface-overlay border border-border-subtle-light dark:border-border-subtle mb-6">
        <button
          type="button"
          onClick={() => setActiveTab("prompt2bot")}
          className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
            activeTab === "prompt2bot" || activeTab === "desktop"
              ? "bg-accent text-white shadow-sm"
              : "text-text-secondary-light dark:text-text-secondary"
          }`}
        >
          prompt2bot
        </button>
        <button
          type="button"
          onClick={() => setActiveTab("openclaw")}
          className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
            activeTab === "openclaw"
              ? "bg-surface-raised-light dark:bg-surface-raised text-text-primary-light dark:text-text-primary shadow-sm"
              : "text-text-secondary-light dark:text-text-secondary"
          }`}
        >
          OpenClaw
        </button>
        <button
          type="button"
          onClick={() => setActiveTab("hermes")}
          className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
            activeTab === "hermes"
              ? "bg-surface-raised-light dark:bg-surface-raised text-text-primary-light dark:text-text-primary shadow-sm"
              : "text-text-secondary-light dark:text-text-secondary"
          }`}
        >
          Hermes Agent
        </button>
      </div>

      {/* Desktop Table View */}
      <div className="hidden md:block overflow-x-auto rounded-2xl border border-border-subtle-light dark:border-border-subtle bg-surface-raised-light dark:bg-surface-raised shadow-md">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="border-b border-border-subtle-light dark:border-border-subtle bg-surface-overlay-light/60 dark:bg-surface-overlay/60">
              <th className="py-4 px-5 text-sm font-semibold text-text-secondary-light dark:text-text-secondary w-1/4">
                Capability / Architecture
              </th>
              <th className="py-4 px-5 text-sm font-bold text-accent-light dark:text-accent bg-accent/5 dark:bg-accent/10 border-x border-accent/20 w-1/3">
                <div className="flex items-center gap-2">
                  <span>prompt2bot</span>
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-accent text-white font-medium">
                    Production Ready
                  </span>
                </div>
              </th>
              <th className="py-4 px-5 text-sm font-semibold text-text-primary-light dark:text-text-primary w-1/5">
                OpenClaw
              </th>
              <th className="py-4 px-5 text-sm font-semibold text-text-primary-light dark:text-text-primary w-1/5">
                Hermes Agent
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border-subtle-light dark:divide-border-subtle text-sm">
            {comparisonData.map((item, idx) => (
              <tr
                key={idx}
                className="hover:bg-surface-overlay-light/30 dark:hover:bg-surface-raised/40 transition-colors"
              >
                <td className="py-4 px-5 align-top">
                  <div className="font-semibold text-text-primary-light dark:text-text-primary">
                    {item.feature}
                  </div>
                  <div className="text-xs text-text-tertiary-light dark:text-text-tertiary mt-0.5">
                    {item.category}
                  </div>
                </td>
                <td className="py-4 px-5 align-top bg-accent/5 dark:bg-accent/10 border-x border-accent/20">
                  <div className="flex items-start gap-2">
                    <StatusIcon status={item.prompt2bot.status} />
                    <div>
                      <div className="font-semibold text-text-primary-light dark:text-text-primary leading-snug">
                        {item.prompt2bot.highlight}
                      </div>
                      <p className="text-xs text-text-secondary-light dark:text-text-secondary mt-1 leading-relaxed">
                        {item.prompt2bot.description}
                      </p>
                    </div>
                  </div>
                </td>
                <td className="py-4 px-5 align-top">
                  <div className="flex items-start gap-2">
                    <StatusIcon status={item.openclaw.status} />
                    <div>
                      <div className="font-medium text-text-primary-light dark:text-text-primary leading-snug">
                        {item.openclaw.highlight}
                      </div>
                      <p className="text-xs text-text-tertiary-light dark:text-text-tertiary mt-1 leading-relaxed">
                        {item.openclaw.description}
                      </p>
                    </div>
                  </div>
                </td>
                <td className="py-4 px-5 align-top">
                  <div className="flex items-start gap-2">
                    <StatusIcon status={item.hermes.status} />
                    <div>
                      <div className="font-medium text-text-primary-light dark:text-text-primary leading-snug">
                        {item.hermes.highlight}
                      </div>
                      <p className="text-xs text-text-tertiary-light dark:text-text-tertiary mt-1 leading-relaxed">
                        {item.hermes.description}
                      </p>
                    </div>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Card View */}
      <div className="md:hidden space-y-4">
        {comparisonData.map((item, idx) => {
          const selectedPlatform =
            activeTab === "desktop" || activeTab === "prompt2bot"
              ? { name: "prompt2bot", data: item.prompt2bot, isPrimary: true }
              : activeTab === "openclaw"
              ? { name: "OpenClaw", data: item.openclaw, isPrimary: false }
              : { name: "Hermes Agent", data: item.hermes, isPrimary: false };

          return (
            <div
              key={idx}
              className={`p-4 rounded-xl border ${
                selectedPlatform.isPrimary
                  ? "border-accent/40 bg-accent/5 dark:bg-accent/10 shadow-sm"
                  : "border-border-subtle-light dark:border-border-subtle bg-surface-raised-light dark:bg-surface-raised"
              }`}
            >
              <div className="flex items-center justify-between gap-2 mb-2">
                <span className="text-xs font-semibold text-text-tertiary-light dark:text-text-tertiary uppercase tracking-wider">
                  {item.category}
                </span>
                <span
                  className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${
                    cellBadgeClass(
                      selectedPlatform.data.status,
                    )
                  }`}
                >
                  {selectedPlatform.name}
                </span>
              </div>
              <h4 className="text-sm font-bold text-text-primary-light dark:text-text-primary mb-2">
                {item.feature}
              </h4>
              <div className="flex items-start gap-2">
                <StatusIcon status={selectedPlatform.data.status} />
                <div>
                  <div className="text-xs font-semibold text-text-primary-light dark:text-text-primary leading-snug">
                    {selectedPlatform.data.highlight}
                  </div>
                  <p className="text-xs text-text-secondary-light dark:text-text-secondary mt-1 leading-relaxed">
                    {selectedPlatform.data.description}
                  </p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Rationale & Deep Dive Links */}
      <div className="mt-8 p-5 rounded-xl border border-border-subtle-light dark:border-border-subtle bg-surface-overlay-light/30 dark:bg-surface-raised/20 flex flex-col sm:flex-row items-center justify-between gap-4 text-center sm:text-left">
        <div>
          <h4 className="text-sm font-bold text-text-primary-light dark:text-text-primary">
            Read the Architectural Deep Dives
          </h4>
          <p className="text-xs text-text-secondary-light dark:text-text-secondary mt-0.5">
            Learn why decoupling compute, isolating multi-tenant memory, and
            adding Safescript makes production AI reliable.
          </p>
        </div>
        <div className="flex flex-wrap items-center justify-center gap-2">
          <a
            href="/blog/whats-unique-in-prompt2bot"
            className="group text-xs font-semibold px-3 py-1.5 rounded-lg bg-surface-raised-light dark:bg-surface-raised border border-border-subtle-light dark:border-border-subtle hover:border-accent/40 text-accent-light dark:text-accent transition-colors inline-flex items-center gap-1.5"
          >
            <span>What's Unique</span>
            <ArrowRightIcon className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" />
          </a>
          <a
            href="/blog/common-weaknesses-and-scale-issues-with-popular-harnesses"
            className="group text-xs font-semibold px-3 py-1.5 rounded-lg bg-surface-raised-light dark:bg-surface-raised border border-border-subtle-light dark:border-border-subtle hover:border-accent/40 text-accent-light dark:text-accent transition-colors inline-flex items-center gap-1.5"
          >
            <span>Harness Scale Issues</span>
            <ArrowRightIcon className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" />
          </a>
        </div>
      </div>
    </div>
  );
};

export const ComparisonSection = () => (
  <section className="min-h-screen flex flex-col items-center justify-center w-full max-w-5xl mx-auto px-6 py-16 md:py-20">
    <div className="text-center mb-10">
      <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-accent/10 text-accent-light dark:text-accent border border-accent/20 mb-3">
        Architectural Comparison
      </div>
      <h2 className="text-3xl md:text-4xl font-display font-bold text-text-primary-light dark:text-text-primary mb-3">
        Why prompt2bot vs. Local Harnesses?
      </h2>
      <p className="text-base md:text-lg text-text-secondary-light dark:text-text-secondary max-w-2xl mx-auto">
        Local-first frameworks like OpenClaw and Hermes are great for solo
        experiments in a terminal. Here is how prompt2bot is architected for
        secure, multi-user production.
      </p>
    </div>
    <FeatureComparisonTable />
  </section>
);
